import { Check, List, MoreHorizontal, Search, Users } from 'lucide-react';
import { AppSwitcher } from '@/components/AppSwitcher';
import { RealtimeStatusPill } from '@/components/RealtimeStatusPill';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MailViewTabs } from '@/features/mailbox/MailViewTabs';
import { useUIStore } from '@/stores/uiStore';
import { useAccountStore } from '@/stores/accountStore';
import { useFolderStore } from '@/stores/folderStore';
import { useSenderGroupingStore } from '@/stores/senderGroupingStore';
import { useCalendarViewStore } from '@/stores/calendarViewStore';
import { useCommandPaletteStore } from '@/stores/commandPaletteStore';
import type { CalendarViewMode } from '@/types/calendar';
import { cn } from '@/lib/utils';

export const CALENDAR_VIEWS: Array<{ id: CalendarViewMode; label: string }> = [
  { id: 'day', label: 'Day' },
  { id: 'week', label: 'Week' },
  { id: 'month', label: 'Month' },
  { id: 'year', label: 'Year' },
  { id: 'agenda', label: 'Agenda' },
];

/**
 * The content column's top bar (round 4.2): sits to the right of the rail,
 * level with the BrandCap, reading left to right as one sentence — which app
 * › which view of it — then search at the far edge. Desktop only; mobile
 * carries the same structure in the dock.
 */
export function WorkspaceHeader() {
  const activeWorkspace = useUIStore((state) => state.activeWorkspace);

  return (
    <header className="hidden h-13 shrink-0 items-center gap-2 px-4 md:flex">
      <AppSwitcher />
      {activeWorkspace === 'mail' && (
        <>
          <MailViewTabs />
          <MailViewOptionsMenu />
        </>
      )}
      {activeWorkspace === 'calendar' && <CalendarViewTabs />}
      <div className="ml-auto flex items-center gap-2">
        <RealtimeStatusPill />
        <HeaderSearch />
      </div>
    </header>
  );
}

/**
 * View options for the mail list (layout individual/groups), stuck to the
 * right end of the tab strip. Only meaningful on the Inbox tab.
 */
function MailViewOptionsMenu() {
  const mailView = useUIStore((state) => state.mailView);
  const selectedAccountId = useAccountStore((state) => state.selectedAccountId);
  const folders = useFolderStore((state) => state.folders);
  const selectedFolderByAccount = useFolderStore((state) => state.selectedFolderByAccount);
  const inboxMode = useSenderGroupingStore((state) => state.inboxMode);
  const setInboxMode = useSenderGroupingStore((state) => state.setInboxMode);
  const groupBySender = useSenderGroupingStore((state) => state.groupBySender);
  const toggleGroupBySender = useSenderGroupingStore((state) => state.toggleGroupBySender);

  if (mailView !== 'mailbox') {
    return null;
  }

  const selectedFolderId = selectedAccountId
    ? selectedFolderByAccount[selectedAccountId] ?? ''
    : '';
  const folder = folders.find((f) => f.id === selectedFolderId);
  const role = folder?.role ?? folder?.unifiedRole ?? null;
  const isInbox = role === 'inbox';
  const groupedLabel = role === 'sent' ? 'Grouped by recipient' : 'Grouped by sender';
  const grouped = isInbox ? inboxMode === 'senders' : groupBySender;

  const setGrouped = (next: boolean) => {
    if (isInbox) {
      void setInboxMode(next ? 'senders' : 'flat');
    } else {
      void toggleGroupBySender(next);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 shrink-0 rounded-full p-0 text-muted-foreground/70 hover:text-foreground"
          aria-label="View options"
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel className="text-xs uppercase tracking-wide text-muted-foreground">
          Layout
        </DropdownMenuLabel>
        <DropdownMenuItem onClick={() => setGrouped(false)} className="gap-2">
          <List className="h-4 w-4" />
          Individual emails
          {!grouped && <Check className="ml-auto h-4 w-4" />}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setGrouped(true)} className="gap-2">
          <Users className="h-4 w-4" />
          {groupedLabel}
          {grouped && <Check className="ml-auto h-4 w-4" />}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Calendar's view switcher, styled identically to the mail tabs. */
function CalendarViewTabs({ className }: { className?: string }) {
  const activeView = useCalendarViewStore((state) => state.activeView);
  const setActiveView = useCalendarViewStore((state) => state.setActiveView);
  return (
    <Tabs
      value={activeView}
      onValueChange={(value) => setActiveView(value as CalendarViewMode)}
      className={cn('min-w-0 gap-0', className)}
    >
      <TabsList className="gap-0.5 rounded-full p-1">
        {CALENDAR_VIEWS.map(({ id, label }) => (
          <TabsTrigger
            key={id}
            value={id}
            className={cn(
              'rounded-full px-3 text-muted-foreground hover:text-foreground',
              'data-[state=active]:bg-card data-[state=active]:text-primary',
              'dark:data-[state=active]:bg-card dark:data-[state=active]:text-primary',
              'dark:data-[state=active]:border-transparent'
            )}
          >
            {label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

/**
 * The header search field. Clicking it opens the command palette anchored to
 * this input (the palette's own input takes its place); ⌘K still opens the
 * centered dialog.
 */
function HeaderSearch() {
  const open = useCommandPaletteStore((state) => state.open);

  return (
    <button
      type="button"
      onClick={(event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        open({ top: rect.top, left: rect.left, right: rect.right, width: rect.width });
      }}
      className={cn(
        // Icon-only below lg: between md and lg (the Duo's inner screen in
        // landscape) the field's width is what the tab strip needs.
        'flex h-9 w-9 lg:w-56 shrink-0 items-center justify-center lg:justify-start gap-2 rounded-full bg-card lg:px-3.5 text-sm text-muted-foreground',
        'shadow-xs border border-border/60 hover:border-border transition-colors'
      )}
      aria-label="Search"
    >
      <Search className="h-4 w-4 shrink-0 opacity-60" />
      <span className="hidden flex-1 truncate text-left lg:inline">Search…</span>
      <kbd className="hidden lg:pointer-fine:inline rounded bg-muted px-1.5 font-sans text-[10px] font-medium text-muted-foreground/80">
        ⌘K
      </kbd>
    </button>
  );
}
